import { ConfigProvider, Table } from 'antd'
import { ColumnsType } from 'antd/es/table'
import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { TeamSnapshotWithUser } from '@/api/type-response'
import { Checkbox, Trans } from '@/components'
import { ActionMenu } from '@/components/base/ActionMenu'
import { EmptyView } from '@/components/EmptyView'
import { SNAP_STATUS_MAP } from '@/consts/snapshot'
import { Snapshot } from '@/models/snapshot'
import { EditableSnapshotName } from '@/pages/snapshots/components/EditableSnapshotName'
import { formatBytes, formatDatePreset } from '@/utils/formatter'
import { cn } from '@/utils/style'

type SnapshotRecord = Snapshot | TeamSnapshotWithUser

interface ActionItem {
  label: ReactNode
  onClick: () => void
  disabled?: boolean
  type?: 'default' | 'danger'
  show?: boolean
}

interface SnapshotTableProps<T extends SnapshotRecord> {
  snapshots: T[]
  selectedIds: string[]
  onSelectionChange: (ids: string[]) => void
  variant?: 'admin' | 'member'
  showCheckbox?: boolean
  showOwner?: boolean
  ownerColumnTitle?: ReactNode
  editableName?: boolean
  onNameUpdate?: () => void
  actionItems?: (record: T) => ActionItem[]
  detailLinkPath?: (record: T) => string
  disabled?: boolean
}

/**
 * Unified snapshot table component
 * Supports both admin (TeamSnapshotWithUser) and member (Snapshot) variants
 */
export function SnapshotTable<T extends SnapshotRecord>({
  snapshots,
  selectedIds,
  onSelectionChange,
  variant = 'admin',
  showCheckbox = true,
  showOwner = variant === 'admin',
  ownerColumnTitle,
  editableName = variant === 'member',
  onNameUpdate,
  actionItems,
  detailLinkPath,
  disabled = false,
}: SnapshotTableProps<T>) {
  const { t } = useTranslation()

  // Extract resID from different data structures
  const getResID = (record: T): string => {
    if ('snapshot' in record) {
      return (record as TeamSnapshotWithUser).snapshot.resID
    }
    return (record as Snapshot).resID
  }

  // Extract snapshot object for uniform access
  const getSnapshot = (record: T) => {
    if ('snapshot' in record) {
      return (record as TeamSnapshotWithUser).snapshot
    }
    return record as Snapshot
  }

  const allIds = snapshots.map(getResID)
  const isAllSelected = allIds.length > 0 && selectedIds.length === allIds.length
  const isSomeSelected = selectedIds.length > 0 && selectedIds.length < allIds.length

  const handleSelectAll = (checked: boolean) => {
    if (!disabled) {
      onSelectionChange(checked ? allIds : [])
    }
  }

  const handleSelectRow = (resID: string, checked: boolean) => {
    if (!disabled) {
      if (checked) {
        onSelectionChange([...selectedIds, resID])
      } else {
        onSelectionChange(selectedIds.filter((id) => id !== resID))
      }
    }
  }

  // Determine if Action column should be shown
  // Show column if either actionItems or detailLinkPath is provided
  const hasActions = actionItems !== undefined
  const hasDetailLink = detailLinkPath !== undefined
  const shouldShowActionColumn = hasActions || hasDetailLink

  const columns: ColumnsType<T> = [
    // Checkbox column
    ...(showCheckbox
      ? [
          {
            title: (
              <Checkbox
                checked={isAllSelected}
                onChange={handleSelectAll}
                disabled={disabled}
                className={cn({ 'after:opacity-50': isSomeSelected && !isAllSelected })}
              />
            ),
            key: 'checkbox',
            width: 60,
            fixed: 'left' as const,
            render: (_: unknown, record: T) => (
              <Checkbox
                checked={selectedIds.includes(getResID(record))}
                onChange={(checked) => handleSelectRow(getResID(record), checked)}
                disabled={disabled}
              />
            ),
          },
        ]
      : []),

    // ID column
    {
      title: <Trans i18nKey="instance.tableHeaderID">ID</Trans>,
      key: 'resID',
      width: 150,
      className: 't-sm',
      fixed: 'left',
      render: (_: unknown, record: T) => getResID(record),
    },

    // Name column
    {
      title: <Trans i18nKey="instance.tableHeaderName">Name</Trans>,
      key: 'name',
      width: editableName ? 300 : 208,
      className: 't-sm',
      ellipsis: true,
      render: (_: unknown, record: T) => {
        const snapshot = getSnapshot(record)
        if (editableName && onNameUpdate && snapshot instanceof Snapshot && !disabled) {
          return (
            <div className="flex items-center">
              <EditableSnapshotName snapshot={snapshot} onUpdate={onNameUpdate} />
            </div>
          )
        }
        // Snapshot class has 'name', ServerSnapInfo has 'flag.subject'
        if (snapshot instanceof Snapshot) {
          return snapshot.name || '-'
        }
        return snapshot.flag?.subject || '-'
      },
    },

    // Owner column (only for admin variant)
    ...(showOwner
      ? [
          {
            title: ownerColumnTitle ?? <Trans i18nKey="team.admin.table.owner">Owner</Trans>,
            key: 'owner',
            width: 148,
            className: 't-sm',
            render: (_: unknown, record: T) => {
              if ('user' in record) {
                const teamRecord = record as TeamSnapshotWithUser
                return teamRecord.user.user.nickname || teamRecord.user.user.name
              }
              return '-'
            },
          },
        ]
      : []),

    // Status column
    {
      title: <Trans i18nKey="team.admin.table.status">Status</Trans>,
      key: 'status',
      width: 120,
      className: 't-sm',
      render: (_: unknown, record: T) => {
        const snapshot = getSnapshot(record)
        // Use centralized status mapping
        const statusInfo = SNAP_STATUS_MAP[snapshot.status as keyof typeof SNAP_STATUS_MAP]
        return <span className={cn('t-sm', statusInfo.style)}>{t(statusInfo.labelKey)}</span>
      },
    },

    // Created Time column
    {
      title: <Trans i18nKey="teamSnapshot.admin.createdTime">Created Time</Trans>,
      key: 'createdTime',
      width: 180,
      className: 't-sm text-tertiary',
      render: (_: unknown, record: T) => {
        const snapshot = getSnapshot(record)
        if (snapshot instanceof Snapshot) {
          return formatDatePreset(snapshot.createdAt, 'minute')
        }
        return formatDatePreset(snapshot.requestTime, 'minute')
      },
    },

    // Size column
    {
      title: <Trans i18nKey="teamSnapshot.admin.size">Size</Trans>,
      key: 'size',
      width: 120,
      className: 't-sm',
      fixed: 'right',
      render: (_: unknown, record: T) => {
        const snapshot = getSnapshot(record)
        if (snapshot instanceof Snapshot) {
          return formatBytes(snapshot.size)
        }
        const sizeBytes =
          typeof snapshot.sizeByte === 'string'
            ? parseInt(snapshot.sizeByte, 10)
            : snapshot.sizeByte
        return formatBytes(sizeBytes)
      },
    },

    // Action column
    ...(shouldShowActionColumn
      ? [
          {
            title: <Trans i18nKey="instance.tableHeaderAction">Action</Trans>,
            key: 'action',
            width: 150,
            className: 't-sm !text-right',
            fixed: 'right' as const,
            render: (_: unknown, record: T) => {
              const items = actionItems
                ? actionItems(record).filter((item) => item.show !== false)
                : []

              return (
                <div className="flex gap-3 items-center justify-end">
                  {detailLinkPath && (
                    <Link
                      to={disabled ? '#' : detailLinkPath(record)}
                      state={{ snapshot: record }}
                      onClick={(e) => disabled && e.preventDefault()}
                      className={cn('text-btn-secondary t-sb', {
                        'opacity-50 cursor-not-allowed pointer-events-none': disabled,
                        'cursor-pointer': !disabled,
                      })}
                    >
                      <Trans i18nKey="team.admin.table.details">Details</Trans>
                    </Link>
                  )}
                  {items.length > 0 && <ActionMenu items={items} popupContainer="body" />}
                </div>
              )
            },
          },
        ]
      : []),
  ]

  return (
    <ConfigProvider
      theme={{
        components: {
          Table: {
            headerBg: 'rgb(var(--color-panel-white))',
            headerColor: 'rgb(var(--color-text-secondary))',
            rowHoverBg: 'rgb(var(--color-table-row-selected))',
          },
        },
      }}
    >
      <Table
        rowKey={(record) => getResID(record)}
        columns={columns}
        dataSource={snapshots}
        pagination={false}
        scroll={{ x: 'max-content' }}
        className={`snapshot-table snapshot-table-${variant}`}
        locale={{
          emptyText: (
            <EmptyView
              title={
                <span className="t-sm text-secondary">
                  <Trans i18nKey="common.emptyViewNoDataMsg" />
                </span>
              }
            />
          ),
        }}
      />
    </ConfigProvider>
  )
}
